/**
 * Display vocabulary and pure helpers for the Bexio sync-run monitoring page.
 *
 * Everything here is a FORMATTER or a CONFIG. Nothing aggregates: the success
 * rate, the counts and the average duration are computed by PostgreSQL and
 * asserted there (`backend/tests/test_bexio_sync_run_read_postgres.py`), so the
 * only thing this module is allowed to do with them is render them. A second
 * implementation in the browser is how a KPI and the API it came from start
 * disagreeing about the same window.
 */

/**
 * How many rows a page of the run table holds by default.
 *
 * Ten, not twenty-five: the table now shares its screen with a permanent detail
 * column and, below it, the 14-day history chart. A page that fills the viewport
 * on its own pushes both out of sight, and the run list is scanned — page by page —
 * rather than read end to end.
 *
 * LOCAL TO THIS PAGE. The shared `BexioTablePagination` already offers 10/25/50 and
 * its own default is untouched, so no other Bexio table changes size because of
 * this. A reader who wants more rows still picks 25 or 50 in the pager.
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * The four scopes `bexio_sync_runs` can actually hold, with the category each
 * belongs to.
 *
 * THERE IS NO `payments` SCOPE and there never was one: payments are mirrored
 * INSIDE the invoices run, under that run's id. The shared `SCOPE_LABEL` map in
 * `bexioFormat` still carries a historic `payments` entry for the legacy page;
 * this page deliberately keeps its own list so that entry cannot leak into a
 * filter and offer the reader a run type that cannot exist.
 *
 * `category` is what lets the table mark a maintenance or technical run as such.
 * `currency_repair` re-resolves a column on rows that were already mirrored — it
 * fetches nothing from Bexio — and `init` marks a run that could not even build a
 * client. Reading either as "a sync happened" is the mistake the badge prevents.
 */
export const SYNC_RUN_SCOPES = [
  { value: "invoices", label: "Rechnungen", category: "data" },
  { value: "contacts", label: "Kontakte", category: "data" },
  { value: "currency_repair", label: "Währungsprüfung", category: "maintenance" },
  { value: "init", label: "Initialisierung", category: "technical" },
];

/**
 * The scopes that mean DATA actually came from Bexio — the backend's
 * `DATA_SYNC_SCOPES`, and this page's default view.
 *
 * The default is a default, not a restriction: both other scopes stay reachable
 * through the filter. But an operator opening this page is asking "is syncing
 * healthy", and folding maintenance runs into that answer changes the question.
 */
export const DEFAULT_SCOPES = ["invoices", "contacts"];

/** Badge text per category. Data syncs get none — they are the normal case. */
export const SCOPE_CATEGORY_BADGE = {
  maintenance: "Wartung",
  technical: "Technisch",
};

/**
 * Status values the CHECK constraint permits, in the order the filter offers.
 *
 * `partial` is here even though no writer produces it today: the database
 * accepts it, so a row can carry it, and a filter that omitted it would
 * contradict the schema. `skipped` is NOT here — it exists nowhere in the
 * backend, and the historic entry in the shared `SYNC_STATUS_LABEL` map is not
 * evidence that it does.
 */
export const SYNC_RUN_STATUSES = [
  { value: "running", label: "Läuft" },
  { value: "success", label: "Erfolgreich" },
  { value: "error", label: "Fehler" },
  { value: "partial", label: "Teilweise" },
];

/**
 * Status -> label for this page.
 *
 * Deliberately NOT the shared `SYNC_STATUS_LABEL`, which renders `success` as
 * "Erfolg" and is pinned by the overview test. Rewording the shared constant
 * would change the overview preview, which this slice is explicitly not
 * redesigning; keeping a page-local map lets the filter option and the table
 * badge read the same word instead of one saying "Erfolgreich" and the other
 * "Erfolg". `syncStatusTone` from `bexioFormat` IS reused — the colour semantics
 * are shared, only the wording is local.
 */
export const SYNC_RUN_STATUS_LABEL = Object.fromEntries(
  SYNC_RUN_STATUSES.map((s) => [s.value, s.label])
);

/** The selectable monitoring windows. 30 days is the backend's own default. */
export const PERIOD_OPTIONS = [
  { days: 7, label: "Letzte 7 Tage" },
  { days: 30, label: "Letzte 30 Tage" },
  { days: 90, label: "Letzte 90 Tage" },
];

export const DEFAULT_PERIOD_DAYS = 30;

/**
 * The window both the summary and the list are asked for.
 *
 * INSTANTS, NOT CALENDAR DAYS. The backend compares `started_at >= from` and
 * `started_at <= to` against a timestamp column and applies no timezone of its
 * own, so "the last 30 days" is exactly `now - 30 × 24h`. Folding this to a
 * Europe/Zurich day boundary — a 00:00:00 start, a 23:59:59 end — would be the
 * browser inventing a semantic the API does not have, and a bare `YYYY-MM-DD`
 * would parse server-side as midnight and silently drop most of the last day.
 *
 * `now` is a parameter so one instant serves the whole refresh. Calling
 * `Date.now()` separately in the summary and in the list would ask two questions
 * milliseconds apart, and a run started in that gap would appear in one answer
 * and not the other.
 *
 * @param {number} days
 * @param {number|Date} now
 * @returns {{ startedFrom: string, startedTo: string, days: number }}
 */
export function buildSyncRunWindow(days, now) {
  const end = now instanceof Date ? now.getTime() : Number(now);
  const start = end - days * 24 * 60 * 60 * 1000;
  return {
    days,
    startedFrom: new Date(start).toISOString(),
    startedTo: new Date(end).toISOString(),
  };
}

/** The German label of the selected window, for the KPI subtitle. */
export function periodLabel(days) {
  const found = PERIOD_OPTIONS.find((p) => p.days === days);
  return found ? found.label : `Letzte ${days} Tage`;
}

/**
 * Seconds -> a readable duration.
 *
 * Under a minute reads as plain seconds; above it, minutes plus zero-padded
 * seconds, so a column of durations stays aligned. Fractions are rounded away
 * before formatting — the backend's average is a real mean and can carry five
 * decimals, none of which an operator can act on.
 *
 * There is no hour tier: a sync run measured in hours is itself the finding, and
 * "94 Min. 12 s" says that more plainly than "1 Std. 34 Min." would.
 *
 * Missing or negative input returns a dash rather than a number. A negative
 * duration means `finished_at` precedes `started_at`, which is broken data;
 * `Math.abs` would hide that behind a plausible-looking figure.
 */
export function formatDurationSeconds(seconds) {
  if (seconds == null || seconds === "") return "—";
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return "—";
  const total = Math.round(n);
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes} Min. ${String(rest).padStart(2, "0")} s`;
}

/**
 * How long ONE run took, from its own two timestamps.
 *
 * Allowed here — and only here — because it is display arithmetic over a single
 * row the reader is looking at, not a statistic over a set. The average across
 * runs is a different question and comes from the server.
 *
 * `null` while the run is still going: it has no end yet, and treating "not
 * finished" as a duration of zero would be a claim about a run in flight.
 */
export function runDurationSeconds(run) {
  if (!run?.started_at || !run?.finished_at) return null;
  const start = new Date(run.started_at).getTime();
  const end = new Date(run.finished_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return (end - start) / 1000;
}

/**
 * The backend's success rate, formatted.
 *
 * IT IS ALREADY A PERCENTAGE — `success / (success + error + partial) × 100`,
 * computed in SQL. Multiplying by 100 here would turn 92.8 into 9280 %. The only
 * work left is the decimal and the sign.
 *
 * `null` stays `null`: the backend returns it when no run in the window has
 * reached an outcome, and "nothing has finished" must not render as "0 %
 * succeeded". The caller decides what to say instead.
 */
export function formatSuccessRate(rate) {
  if (rate == null) return null;
  const n = Number(rate);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString("de-CH", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

/** Scope -> its config entry, or `null` for a value this page does not know. */
export function scopeConfig(value) {
  return SYNC_RUN_SCOPES.find((s) => s.value === value) || null;
}

/** Scope -> German label, falling back to the raw value rather than inventing one. */
export function scopeLabel(value) {
  return scopeConfig(value)?.label || value || "—";
}

/** Scope -> "Wartung" / "Technisch", or `null` for a normal data sync. */
export function scopeCategoryBadge(value) {
  const cfg = scopeConfig(value);
  return cfg ? SCOPE_CATEGORY_BADGE[cfg.category] || null : null;
}

/** Status -> German label for this page. */
export function statusLabel(value) {
  return SYNC_RUN_STATUS_LABEL[value] || value || "—";
}

/**
 * A counter for display. 0 is a real count and renders as "0"; only a MISSING
 * value becomes a dash.
 *
 * Shared by the KPI row and the table since SYNC-3a — it was page-local while
 * both lived in one file, and two copies of a three-line helper is how "0" and
 * "—" start meaning different things in two places on the same screen.
 */
export function fmtCount(v) {
  return v == null ? "—" : String(v);
}
